/**
 * Captures diagnostic data for rich feedback submissions:
 *   - Console errors/warnings (ring buffer)
 *   - Network errors (via axios interceptor)
 *   - ALL recent API calls (success + failure)
 *   - Navigation history (SPA route changes)
 *   - Interaction trail (clicks + keypresses)
 *   - Unhandled promise rejections
 *   - DOM state snapshot (focused element, open dialogs)
 *   - localStorage / sessionStorage key names (values redacted)
 *   - Performance info, timezone, locale, connection type
 *
 * Call initErrorCapture() once before React mounts.
 */
import { APP_VERSION } from '../constants/changelog';

const MAX_CONSOLE = 30;
const MAX_NETWORK = 15;
const MAX_API = 20;
const MAX_NAV = 20;
const MAX_INTERACTIONS = 25;
const MAX_REJECTIONS = 10;

const SENSITIVE_PATTERN = /(?:key|token|password|secret|authorization|bearer)[=:]\s*\S+/gi;
function scrub(str: string): string {
  return str.replace(SENSITIVE_PATTERN, '[REDACTED]');
}

export interface ConsoleEntry { level: 'error' | 'warn'; message: string; timestamp: string }
export interface NetworkEntry { url: string; method: string; status: number; timestamp: string }
export interface ApiCallEntry { url: string; method: string; status: number; durationMs: number; timestamp: string }
export interface NavEntry { url: string; timestamp: string; title?: string }
export interface InteractionEntry { type: 'click' | 'keydown' | 'submit' | 'change'; target: string; detail?: string; timestamp: string }
export interface RejectionEntry { reason: string; timestamp: string }

const consoleBuffer: ConsoleEntry[] = [];
const networkBuffer: NetworkEntry[] = [];
const apiCallBuffer: ApiCallEntry[] = [];
const navHistory: NavEntry[] = [];
const interactionBuffer: InteractionEntry[] = [];
const rejectionBuffer: RejectionEntry[] = [];
const sessionStart = new Date().toISOString();

let initialized = false;

/** Build a short, readable selector for a DOM element. */
function describeElement(el: Element | null): string {
  if (!el) return '(none)';
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const classes = el.className && typeof el.className === 'string'
    ? `.${el.className.trim().split(/\s+/).slice(0, 2).join('.')}`
    : '';
  const testId = el.getAttribute('data-testid');
  const ariaLabel = el.getAttribute('aria-label');
  const name = el.getAttribute('name');
  const text = el.textContent?.trim().slice(0, 40);
  const attr = testId ? `[data-testid="${testId}"]` : ariaLabel ? `[aria-label="${ariaLabel.slice(0, 30)}"]` : name ? `[name="${name}"]` : '';
  const label = tag === 'button' || tag === 'a' ? ` "${text}"` : '';
  return `${tag}${id}${classes}${attr}${label}`.slice(0, 150);
}

export function initErrorCapture(): void {
  if (initialized) return;
  initialized = true;

  navHistory.push({ url: window.location.href, timestamp: sessionStart, title: document.title });

  // ── Console patching ──
  const origError = console.error;
  console.error = (...args: unknown[]) => {
    consoleBuffer.push({ level: 'error', message: scrub(args.map(String).join(' ')).slice(0, 1000), timestamp: new Date().toISOString() });
    if (consoleBuffer.length > MAX_CONSOLE) consoleBuffer.shift();
    origError.apply(console, args);
  };
  const origWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    consoleBuffer.push({ level: 'warn', message: scrub(args.map(String).join(' ')).slice(0, 1000), timestamp: new Date().toISOString() });
    if (consoleBuffer.length > MAX_CONSOLE) consoleBuffer.shift();
    origWarn.apply(console, args);
  };

  // ── SPA navigation tracking ──
  const trackNav = () => {
    const last = navHistory[navHistory.length - 1];
    if (last?.url !== window.location.href) {
      navHistory.push({ url: window.location.href, timestamp: new Date().toISOString(), title: document.title });
      if (navHistory.length > MAX_NAV) navHistory.shift();
    }
  };
  window.addEventListener('popstate', trackNav);
  const origPush = history.pushState;
  history.pushState = function (...args) { origPush.apply(this, args); trackNav(); };
  const origReplace = history.replaceState;
  history.replaceState = function (...args) { origReplace.apply(this, args); trackNav(); };

  // ── Click tracking ──
  window.addEventListener('click', (e) => {
    const el = e.target as Element | null;
    if (!el) return;
    // Walk up to find a meaningful target (button, link, etc.)
    const meaningful = el.closest('button, a, [role="button"], input, select, textarea, [data-testid]') || el;
    interactionBuffer.push({
      type: 'click',
      target: describeElement(meaningful),
      timestamp: new Date().toISOString(),
    });
    if (interactionBuffer.length > MAX_INTERACTIONS) interactionBuffer.shift();
  }, true);

  // ── Keydown tracking (keys only, never values) ──
  window.addEventListener('keydown', (e) => {
    // Skip letter/number keys — too noisy
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) return;
    const el = e.target as Element | null;
    interactionBuffer.push({
      type: 'keydown',
      target: describeElement(el),
      detail: [e.ctrlKey && 'Ctrl', e.metaKey && 'Cmd', e.altKey && 'Alt', e.shiftKey && 'Shift', e.key].filter(Boolean).join('+'),
      timestamp: new Date().toISOString(),
    });
    if (interactionBuffer.length > MAX_INTERACTIONS) interactionBuffer.shift();
  }, true);

  // ── Form submit tracking ──
  window.addEventListener('submit', (e) => {
    interactionBuffer.push({
      type: 'submit',
      target: describeElement(e.target as Element),
      timestamp: new Date().toISOString(),
    });
    if (interactionBuffer.length > MAX_INTERACTIONS) interactionBuffer.shift();
  }, true);

  // ── Unhandled promise rejections ──
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason instanceof Error ? `${e.reason.name}: ${e.reason.message}` : String(e.reason);
    rejectionBuffer.push({ reason: scrub(reason).slice(0, 500), timestamp: new Date().toISOString() });
    if (rejectionBuffer.length > MAX_REJECTIONS) rejectionBuffer.shift();
  });
}

/** Called from the apiClient response interceptor. Records both success and failure. */
export function captureApiCall(url: string, method: string, status: number, durationMs: number): void {
  apiCallBuffer.push({
    url: scrub(url),
    method: method.toUpperCase(),
    status,
    durationMs: Math.round(durationMs),
    timestamp: new Date().toISOString(),
  });
  if (apiCallBuffer.length > MAX_API) apiCallBuffer.shift();

  // Also record in networkBuffer if it's an error (for backward compat)
  if (status >= 400) {
    networkBuffer.push({ url: scrub(url), method: method.toUpperCase(), status, timestamp: new Date().toISOString() });
    if (networkBuffer.length > MAX_NETWORK) networkBuffer.shift();
  }
}

/** @deprecated Use captureApiCall instead — kept for apiClient compat */
export function captureNetworkError(url: string, method: string, status: number): void {
  captureApiCall(url, method, status, 0);
}

export const getConsoleErrors = (): ConsoleEntry[] => [...consoleBuffer];
export const getNetworkErrors = (): NetworkEntry[] => [...networkBuffer];
export const getApiCalls = (): ApiCallEntry[] => [...apiCallBuffer];
export const getNavHistory = (): NavEntry[] => [...navHistory];
export const getInteractions = (): InteractionEntry[] => [...interactionBuffer];
export const getRejections = (): RejectionEntry[] => [...rejectionBuffer];

function getSessionDuration(): string {
  const ms = Date.now() - new Date(sessionStart).getTime();
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function getBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) return `Edge ${ua.split('Edg/')[1]?.split(' ')[0]}`;
  if (ua.includes('Chrome/')) return `Chrome ${ua.split('Chrome/')[1]?.split(' ')[0]}`;
  if (ua.includes('Firefox/')) return `Firefox ${ua.split('Firefox/')[1]?.split(' ')[0]}`;
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return `Safari ${ua.split('Version/')[1]?.split(' ')[0]}`;
  return ua;
}

function getOSName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Windows NT 10')) return 'Windows 10/11';
  if (ua.includes('Windows NT')) return 'Windows';
  if (ua.includes('Mac OS X')) return `macOS ${ua.split('Mac OS X ')[1]?.split(')')[0]?.replace(/_/g, '.')}`;
  if (ua.includes('Linux')) return 'Linux';
  return navigator.platform;
}

function getConnectionType(): string {
  const c = (navigator as any).connection;
  if (!c) return 'unknown';
  return [c.effectiveType, c.downlink ? `${c.downlink}Mbps` : null, c.rtt ? `${c.rtt}ms RTT` : null].filter(Boolean).join(', ');
}

function getPerformanceInfo(): string {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (!nav) return 'N/A';
  const mem = (performance as any).memory;
  const parts = [
    `load: ${Math.round(nav.loadEventEnd)}ms`,
    `domReady: ${Math.round(nav.domContentLoadedEventEnd)}ms`,
    `ttfb: ${Math.round(nav.responseStart)}ms`,
  ];
  if (mem) parts.push(`heap: ${Math.round(mem.usedJSHeapSize / 1048576)}MB/${Math.round(mem.jsHeapSizeLimit / 1048576)}MB`);
  return parts.join(' · ');
}

/** Is the element visually present (not hidden by CSS, non-zero box)? */
function isVisible(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  try {
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
  } catch { /* some elements throw */ }
  return true;
}

function labelOf(el: Element): string {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.slice(0, 60);
  const text = (el as HTMLElement).innerText?.trim();
  if (text) return text.slice(0, 60);
  const title = el.getAttribute('title');
  if (title) return title.slice(0, 60);
  return '(unlabeled)';
}

function getDomState(): string {
  const active = document.activeElement;
  const openDialogs = document.querySelectorAll('[role="dialog"]').length;

  const errors = Array.from(document.querySelectorAll('.MuiFormHelperText-root.Mui-error, [aria-invalid="true"]'))
    .map((el) => (el as HTMLElement).innerText?.trim() || describeElement(el))
    .filter(Boolean)
    .slice(0, 8);

  // RBAC-relevant: every visible button/link and whether it's enabled
  const buttons = Array.from(document.querySelectorAll('button, [role="button"], a[href]'))
    .filter(isVisible)
    .slice(0, 60)
    .map((el) => {
      const htmlBtn = el as HTMLButtonElement;
      const disabled = htmlBtn.disabled || el.getAttribute('aria-disabled') === 'true' || el.hasAttribute('disabled');
      return {
        label: labelOf(el),
        tag: el.tagName.toLowerCase(),
        disabled,
      };
    })
    .filter((b) => b.label !== '(unlabeled)' || b.tag === 'button'); // drop noisy a-tags with no text

  // RBAC-relevant: every visible input with name/type/disabled/readonly/required/invalid
  // (we never capture VALUES — privacy)
  const inputs = Array.from(document.querySelectorAll('input, select, textarea'))
    .filter(isVisible)
    .slice(0, 60)
    .map((el) => {
      const input = el as HTMLInputElement;
      return {
        name: input.name || el.id || el.getAttribute('aria-label') || '(unnamed)',
        type: input.type || el.tagName.toLowerCase(),
        disabled: input.disabled,
        readOnly: input.readOnly || false,
        required: input.required || false,
        invalid: el.getAttribute('aria-invalid') === 'true',
        hasValue: !!input.value && input.type !== 'password',
      };
    });

  return JSON.stringify({
    focusedElement: active && active !== document.body ? describeElement(active) : null,
    focusedValue: active instanceof HTMLInputElement && active.type !== 'password' && active.value
      ? `<value length: ${active.value.length}>` : null,
    openDialogs,
    visibleErrors: errors,
    scrollY: window.scrollY,
    documentTitle: document.title,
    buttons,
    inputs,
  });
}

function getStorageKeys(): string {
  try {
    const local = Object.keys(localStorage);
    const session = Object.keys(sessionStorage);
    return JSON.stringify({ localStorage: local, sessionStorage: session });
  } catch { return '{}'; }
}

function getTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch { return 'unknown'; }
}

function getGitCommit(): string {
  return (import.meta.env?.VITE_GIT_COMMIT as string) || 'dev';
}

/** Collect full metadata payload to send with a feedback submission. */
export function collectMetadata(): Record<string, string> {
  return {
    PageUrl: window.location.href,
    BrowserInfo: getBrowserName(),
    OS: getOSName(),
    ScreenResolution: `${screen.width}x${screen.height}`,
    ViewportSize: `${window.innerWidth}x${window.innerHeight}`,
    AppVersion: APP_VERSION || '',
    SessionDuration: getSessionDuration(),
    NavigationHistory: JSON.stringify(getNavHistory()),
    ConsoleErrors: JSON.stringify(getConsoleErrors()),
    NetworkErrors: JSON.stringify(getNetworkErrors()),
    InteractionTrail: JSON.stringify(getInteractions()),
    RecentApiCalls: JSON.stringify(getApiCalls()),
    UnhandledRejections: JSON.stringify(getRejections()),
    DomState: getDomState(),
    StorageKeys: getStorageKeys(),
    PerformanceInfo: getPerformanceInfo(),
    Timezone: getTimezone(),
    Locale: navigator.language,
    ConnectionType: getConnectionType(),
    GitCommit: getGitCommit(),
    Referrer: document.referrer || '(direct)',
  };
}

/** Short summary for the modal's collapsible "Auto-collected context" section. */
export function getMetadataSummary(): { label: string; value: string }[] {
  const m = collectMetadata();
  const nav = JSON.parse(m.NavigationHistory || '[]') as NavEntry[];
  const errors = JSON.parse(m.ConsoleErrors || '[]') as ConsoleEntry[];
  const apis = JSON.parse(m.RecentApiCalls || '[]') as ApiCallEntry[];
  const actions = JSON.parse(m.InteractionTrail || '[]') as InteractionEntry[];
  const rej = JSON.parse(m.UnhandledRejections || '[]') as RejectionEntry[];

  const items: { label: string; value: string }[] = [
    { label: 'Page', value: m.PageUrl },
    { label: 'Browser / OS', value: `${m.BrowserInfo} · ${m.OS}` },
    { label: 'Screen', value: `${m.ScreenResolution} (viewport ${m.ViewportSize})` },
    { label: 'Timezone / Locale', value: `${m.Timezone} · ${m.Locale}` },
    { label: 'Connection', value: m.ConnectionType },
    { label: 'App Version', value: `${m.AppVersion} (${m.GitCommit})` },
    { label: 'Session', value: m.SessionDuration },
    { label: 'Performance', value: m.PerformanceInfo },
  ];
  if (nav.length > 1) items.push({ label: `Navigation (${nav.length})`, value: nav.slice(-5).map((n) => n.url.replace(window.location.origin, '')).join(' → ') });
  if (actions.length > 0) items.push({ label: `Recent Actions (${actions.length})`, value: actions.slice(-8).map((a) => `${a.type}: ${a.target}${a.detail ? ` [${a.detail}]` : ''}`).join('\n') });
  if (apis.length > 0) items.push({ label: `API Calls (${apis.length})`, value: apis.slice(-8).map((a) => `${a.method} ${a.url} → ${a.status} (${a.durationMs}ms)`).join('\n') });
  if (errors.length > 0) items.push({ label: `Console Errors (${errors.length})`, value: errors.slice(-3).map((e) => `[${e.level}] ${e.message.slice(0, 120)}`).join('\n') });
  if (rej.length > 0) items.push({ label: `Unhandled Rejections (${rej.length})`, value: rej.slice(-3).map((r) => r.reason).join('\n') });
  return items;
}
